import { useEffect, useRef, useState } from 'react';

interface RouletteItem {
  name: string;
  probability: number;
}

interface RouletteState {
  isSpinning: boolean;
  currentRotation: number;
  items: RouletteItem[];
}

interface RouletteOptions {
  items: RouletteItem[];
  borderColor?: string;
  centerButtonColor?: string;
  centerButtonTextColor?: string;
  centerButtonText?: string;
  centerButtonFontSize?: number;
  centerButtonFontWeight?: number;
  centerButtonFontFamily?: string;
  sectorColor?: string;
  itemTextColor?: string;
  itemFontSize?: number;
  itemFontWeight?: number;
  itemFontFamily?: string;
  onSpinEnd?: () => void;
}

// ======== 커스텀 훅: useRoulette ========
export const useRoulette = ({
  items,
  borderColor = '#000000',
  centerButtonColor = '#000000',
  centerButtonTextColor = '#ffffff',
  centerButtonText = 'START',
  centerButtonFontSize = 1.125,
  centerButtonFontWeight = 700,
  centerButtonFontFamily = 'Roboto',
  sectorColor = '#D5D5D5',
  itemTextColor = '#000000',
  itemFontSize = 0.75,
  itemFontWeight = 400,
  itemFontFamily = 'Roboto',
  onSpinEnd
}: RouletteOptions) => {
  // 캔버스 및 컨테이너 레퍼런스
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // 모달(당첨 결과) 표시 여부
  const [showModal, setShowModal] = useState(false);

  // Device Pixel Ratio 저장 (고해상도 디스플레이 지원)
  const dprRef = useRef<number>(1);

  // 룰렛 상태
  const rouletteState = useRef<RouletteState>({
    isSpinning: false,
    currentRotation: -Math.PI / 2, // 초기 각도
    items
  });

  // items가 변경되면 rouletteState 업데이트
  useEffect(() => {
    rouletteState.current.items = items;
  }, [items]);

  // 룰렛 그리기 관련 상수
  const ROULETTE_CONSTANTS = {
    SPIN_DURATION: 3000,
    TOTAL_ROTATIONS: Math.PI * 8,
    INITIAL_ROTATION: -Math.PI / 2
  };

  // ======== Canvas 그리기 관련 함수들 ========
  const drawRoulette = (
    ctx: CanvasRenderingContext2D,
    centerX: number,
    centerY: number,
    radius: number
  ) => {
    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);

    // 배경 색상
    ctx.fillStyle = 'rgba(0, 0, 0, 0)';
    ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);

    // Canvas 기본 스타일 초기화 (검정색 방지)
    ctx.strokeStyle = borderColor;
    ctx.fillStyle = sectorColor;

    // 가장 바깥쪽 테두리
    drawOuterBorder(ctx, centerX, centerY, radius);

    // 룰렛 중심으로 translate & rotate
    ctx.save();
    ctx.translate(centerX, centerY);
    ctx.rotate(rouletteState.current.currentRotation);

    // 슬라이스 그리기
    drawSlices(ctx, radius);

    // 원상 복귀
    ctx.restore();
  };

  const drawOuterBorder = (
    ctx: CanvasRenderingContext2D,
    centerX: number,
    centerY: number,
    radius: number
  ) => {
    ctx.save();
    ctx.translate(centerX, centerY);

    const outerRadius = radius + 15;
    ctx.beginPath();
    ctx.arc(0, 0, outerRadius, 0, Math.PI * 2);
    ctx.strokeStyle = borderColor;
    ctx.lineWidth = 8;
    ctx.stroke();

    // 외곽 점(8개)
    Array.from({ length: 8 }).forEach((_, i) => {
      const angle = (i * Math.PI * 2) / 8;
      const x = Math.cos(angle) * outerRadius;
      const y = Math.sin(angle) * outerRadius;

      ctx.beginPath();
      ctx.arc(x, y, 3, 0, Math.PI * 2);
      ctx.fillStyle = '#ffffff';
      ctx.fill();
    });

    ctx.restore();
  };

  const drawSlices = (ctx: CanvasRenderingContext2D, radius: number) => {
    const { items, currentRotation } = rouletteState.current;
    const sliceAngle = (2 * Math.PI) / items.length;
    const textDistance = radius * 0.6;

    // Convert rem to pixels (assuming 16px base font size)
    const fontSizeInPixels = itemFontSize * 16;

    items.forEach((item, i) => {
      const startAngle = i * sliceAngle;

      ctx.save(); // 각 섹터마다 상태 저장

      // 섹터 그리기
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.arc(0, 0, radius, startAngle, startAngle + sliceAngle);
      ctx.closePath();
      ctx.fillStyle = sectorColor;
      ctx.fill();
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 0.5;
      ctx.stroke();

      // 텍스트 그리기
      ctx.rotate(startAngle + sliceAngle / 2);
      ctx.fillStyle = itemTextColor;
      ctx.font = `${itemFontWeight} ${fontSizeInPixels}px ${itemFontFamily}, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      ctx.translate(textDistance, 0);
      ctx.rotate(Math.PI / 2);
      ctx.fillText(item.name, 0, -15);

      ctx.restore(); // 각 섹터마다 상태 복원
    });

    // 중앙 버튼 그리기
    drawCenterButton(ctx, radius, currentRotation);
  };

  const drawCenterButton = (
    ctx: CanvasRenderingContext2D,
    radius: number,
    currentRotation: number
  ) => {
    const centerRadius = radius * 0.3;
    const centerButtonFontSizeInPixels = centerButtonFontSize * 16;

    ctx.save(); // 중앙 버튼 상태 저장

    ctx.beginPath();
    ctx.arc(0, 0, centerRadius, 0, Math.PI * 2);
    ctx.fillStyle = centerButtonColor;
    ctx.fill();

    // START 버튼 텍스트 (회전 보정)
    ctx.rotate(-currentRotation);
    ctx.fillStyle = centerButtonTextColor;
    ctx.font = `${centerButtonFontWeight} ${centerButtonFontSizeInPixels}px ${centerButtonFontFamily}, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(centerButtonText, 0, 0);

    ctx.restore(); // 중앙 버튼 상태 복원
  };

  // ======== 스핀 로직 관련 함수들 ========
  const spinRoulette = () => {
    if (rouletteState.current.isSpinning) return;

    rouletteState.current.isSpinning = true;

    const startTime = performance.now();
    const spinDuration = 3000; // 3초
    const rotationsPerSecond = 1.5; // 초당 회전 수 (1.5바퀴/초)
    const radiansPerSecond = rotationsPerSecond * Math.PI * 2; // 라디안/초로 변환

    let lastTime = startTime;

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const deltaTime = (currentTime - lastTime) / 1000; // 초 단위로 변환
      lastTime = currentTime;

      if (elapsed < spinDuration) {
        // 시간 기반으로 회전 (모든 기기에서 동일한 속도)
        rouletteState.current.currentRotation += radiansPerSecond * deltaTime;

        const ctx = canvasRef.current?.getContext('2d');
        if (ctx) {
          const dpr = dprRef.current;
          // DPR을 고려한 논리적 크기 계산
          const logicalWidth = ctx.canvas.width / dpr;
          const logicalHeight = ctx.canvas.height / dpr;
          drawRoulette(
            ctx,
            logicalWidth / 2,
            logicalHeight / 2,
            Math.min(logicalWidth, logicalHeight) / 2 - 30
          );
        }
        requestAnimationFrame(animate);
      } else {
        // 3초 후 모달 표시
        setShowModal(true);
        rouletteState.current.isSpinning = false;
        if (onSpinEnd) onSpinEnd();
      }
    };

    requestAnimationFrame(animate);
  };

  // 모달 닫기 및 룰렛 초기화
  const closeModal = () => {
    setShowModal(false);
    rouletteState.current.currentRotation = ROULETTE_CONSTANTS.INITIAL_ROTATION;
    rouletteState.current.isSpinning = false; // 스피닝 상태 초기화

    // 다시 그리기
    const ctx = canvasRef.current?.getContext('2d');
    if (ctx) {
      const dpr = dprRef.current;
      // DPR을 고려한 논리적 크기 계산
      const logicalWidth = ctx.canvas.width / dpr;
      const logicalHeight = ctx.canvas.height / dpr;
      drawRoulette(
        ctx,
        logicalWidth / 2,
        logicalHeight / 2,
        Math.min(logicalWidth, logicalHeight) / 2 - 30
      );
    }
  };

  // Canvas 클릭 이벤트 등록 (canvas가 변경될 때마다 재등록)
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const handleCanvasClick = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      // CSS 크기 기준으로 클릭 좌표 계산 (DPR 무관)
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      // 논리적 크기 기준으로 중심점과 반지름 계산
      const logicalWidth = rect.width;
      const logicalHeight = rect.height;
      const centerX = logicalWidth / 2;
      const centerY = logicalHeight / 2;
      const radius = Math.min(logicalWidth, logicalHeight) / 2 - 30;

      const distance = Math.sqrt(
        Math.pow(x - centerX, 2) + Math.pow(y - centerY, 2)
      );
      // 중앙 버튼(0.3 * radius) 내부 클릭 시 스핀
      if (distance <= radius * 0.3) {
        spinRoulette();
      }
    };

    canvas.addEventListener('click', handleCanvasClick);
    return () => {
      canvas.removeEventListener('click', handleCanvasClick);
    };
  }, [items]); // items가 변경될 때마다 재등록 (canvas remount 시에도 실행됨)

  // 룰렛 그리기 (캔버스 또는 아이템 변경 시)
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    // Device Pixel Ratio 설정 (고해상도 디스플레이 지원)
    const dpr = window.devicePixelRatio || 1;
    dprRef.current = dpr;

    // CSS에서 설정한 크기 가져오기 (또는 기본값 350)
    const displayWidth = canvas.clientWidth || 350;
    const displayHeight = canvas.clientHeight || 350;

    // Canvas 내부 해상도를 DPR에 맞게 설정
    canvas.width = displayWidth * dpr;
    canvas.height = displayHeight * dpr;

    // Context 스케일링 (모든 그리기 작업이 DPR에 맞게 확대됨)
    ctx.scale(dpr, dpr);

    const draw = () => {
      // 스케일링 전 초기화
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.restore();

      // 논리적 크기 기준으로 그리기
      drawRoulette(
        ctx,
        displayWidth / 2,
        displayHeight / 2,
        Math.min(displayWidth, displayHeight) / 2 - 30
      );
    };

    // 커스텀 폰트 로드 후 다시 그리기
    const loadFontsAndDraw = async () => {
      // 먼저 한번 그리기 (fallback 폰트로)
      draw();

      // 폰트가 로드될 때까지 기다린 후 다시 그리기
      if (document.fonts) {
        try {
          await document.fonts.ready;
          // 약간의 지연 후 다시 그리기 (폰트 적용 보장)
          setTimeout(draw, 50);
        } catch (e) {
          // 폰트 로드 실패 시 무시
        }
      }
    };

    loadFontsAndDraw();
  }, [
    items,
    borderColor,
    centerButtonColor,
    centerButtonTextColor,
    centerButtonText,
    centerButtonFontSize,
    centerButtonFontWeight,
    centerButtonFontFamily,
    sectorColor,
    itemTextColor,
    itemFontSize,
    itemFontWeight,
    itemFontFamily
  ]);

  // 훅에서 사용할 값/함수들을 반환
  return {
    containerRef,
    canvasRef,
    showModal,
    setShowModal,
    closeModal,
    spinRoulette
  };
};
